import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon, type IconName } from "../../lib/Icon";
import { Input } from "../../lib/Input";
import { Pill } from "../../lib/Pill";
import { Slats } from "../../lib/Slats";
import { intake } from "../../lib/api";
import { withUtm } from "../../lib/utm";
import { breadcrumbList, faqJsonLd, useSeo } from "../../lib/useSeo";
import { type MigrateSpec } from "./data/migrations";

const WEEKEND_STEPS = [
  {
    n: "01",
    title: "Export your roster to CSV",
    body: "Pull your tenants, units, and balances out of your current software as a spreadsheet. Everything stays yours.",
  },
  {
    n: "02",
    title: "Drop it into NestPlot",
    body: "Our importer auto-detects the columns and shows you a preview of every row. Nothing commits until you say so — no surprises.",
  },
  {
    n: "03",
    title: "Go live — data intact",
    body: "Connect your Stripe and your gate vendor, and you're renting. Migration is included on every plan — the NestPlot team runs the whole thing with you in one 60–90 minute session, no setup fee.",
  },
];

export function MigratePage({ spec }: { spec: MigrateSpec }) {
  useSeo({
    title: spec.metaTitle,
    description: spec.metaDescription,
    path: spec.path,
    jsonLd: [
      breadcrumbList([
        { name: "NestPlot", path: "/" },
        { name: `Migrate from ${spec.competitor}`, path: spec.path },
      ]),
      faqJsonLd(spec.faqs.map(([q, a]) => ({ q, a }))),
    ],
  });

  return (
    <div className="mx-auto max-w-[1080px] px-5 lg:px-8 py-10 lg:py-16">
      {/* Hero */}
      <div className="max-w-[820px]">
        <Pill tone="accent" size="sm">
          {spec.eyebrow} · self-storage
        </Pill>
        <h1 className="t-display-xl mt-4">{spec.h1}</h1>
        <p className="text-sub text-lg mt-4">{spec.sub}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/signup">
            <Btn kind="primary" size="lg" iconRight={<Icon name="arrow-right" size={16} />}>
              Start free — migrate this weekend
            </Btn>
          </Link>
          <Link to="/book-demo">
            <Btn kind="secondary" size="lg">
              Book a 20-min demo
            </Btn>
          </Link>
        </div>
        <p className="text-sub text-xs mt-3">
          30-day trial, no card required. Migration is included on every plan — no setup fee.
        </p>
      </div>

      {/* The squeeze — pain points */}
      <section className="mt-14 border-t border-line pt-12">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          Why operators are leaving {spec.competitor}
        </div>
        <h2 className="t-display-l mt-2">The Storable squeeze is real.</h2>
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          {spec.pains.map((p) => (
            <Card key={p.title}>
              <div className="px-5 py-5">
                <Icon name={p.icon as IconName} size={20} className="text-alert" />
                <div className="font-display font-extrabold text-[16px] mt-3 tracking-[-0.01em]">
                  {p.title}
                </div>
                <p className="text-sub text-sm mt-2">{p.body}</p>
              </div>
            </Card>
          ))}
        </div>
        {spec.proof && (
          <blockquote className="mt-6 border-l-2 border-accent pl-4 max-w-[720px]">
            <p className="text-ink text-lg italic">“{spec.proof.quote}”</p>
            <footer className="text-sub text-sm mt-1">— {spec.proof.source}</footer>
          </blockquote>
        )}
      </section>

      {/* Price + rent comparison band */}
      <section className="mt-14 border-t border-line pt-12">
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <div className="px-6 py-6">
              <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
                {spec.competitor}
              </div>
              <div className="font-display font-black text-[30px] tracking-[-0.03em] leading-tight mt-2 text-alert">
                {spec.pricing.competitor}
              </div>
              <ul className="mt-4 space-y-2 text-sm text-sub">
                <li className="flex items-start gap-2">
                  <Icon name="close" size={14} className="text-alert mt-[3px] shrink-0" />
                  {spec.pricing.setup}
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="close" size={14} className="text-alert mt-[3px] shrink-0" />
                  Forced payment processor — a margin on every tenant charge
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="close" size={14} className="text-alert mt-[3px] shrink-0" />
                  Priced per facility (and per user), so it climbs as you grow
                </li>
              </ul>
              <a
                href={spec.pricing.citation}
                target="_blank"
                rel="noreferrer"
                className="text-sub text-[11px] font-mono mt-4 inline-block hover:text-ink"
              >
                Source: {spec.pricing.citationLabel} →
              </a>
            </div>
          </Card>
          <Card className="shadow-rollup" style={{ border: "1.5px solid var(--accent)" }}>
            <div className="bg-accent px-6 pt-3 pb-2">
              <Slats color="var(--on-accent)" opacity={0.55} height={10} />
            </div>
            <div className="px-6 py-6">
              <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-accent">
                NestPlot
              </div>
              <div className="font-display font-black text-[30px] tracking-[-0.03em] leading-tight mt-2">
                From $59/mo flat
              </div>
              <ul className="mt-4 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Icon name="check" size={14} className="text-ok mt-[3px] shrink-0" />
                  No setup fee — the NestPlot team migrates you
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="check" size={14} className="text-ok mt-[3px] shrink-0" />
                  <span>
                    <strong>Your own Stripe</strong> — 0% of tenant rent to us
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="check" size={14} className="text-ok mt-[3px] shrink-0" />
                  Every feature included · no per-unit, no per-user metering
                </li>
              </ul>
              <Link to="/pricing" className="text-accent text-[11px] font-mono mt-4 inline-block">
                See full pricing →
              </Link>
            </div>
          </Card>
        </div>
      </section>

      {/* Keep your rent wedge */}
      <section className="mt-14 border-t border-line pt-12">
        <div className="rounded-xl bg-surface border border-line px-6 py-8 lg:px-10 lg:py-10">
          <h2 className="t-display-l max-w-[760px]">
            You keep <span className="text-accent">100%</span> of your rent.
          </h2>
          <p className="text-sub mt-3 max-w-[720px]">
            {spec.competitor} routes tenant payments through Storable's own
            processor and keeps a margin. NestPlot connects to <strong>your</strong>{" "}
            Stripe account — every dollar a tenant pays lands directly in your
            bank, and we take 0% of it. By design, we literally can't touch your
            rent.
          </p>
          <div className="mt-5">
            <Link to="/keep-100-of-your-rent" className="text-accent font-semibold text-sm">
              How keeping 100% of your rent works →
            </Link>
          </div>
        </div>
      </section>

      {/* Weekend migration */}
      <section className="mt-14 border-t border-line pt-12">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          The switch
        </div>
        <h2 className="t-display-l mt-2">Migrate this weekend. Save next month.</h2>
        <p className="text-sub mt-3 max-w-[680px]">
          NestPlot's importer reads a CSV exported from {spec.competitor} (or any
          spreadsheet), auto-maps the columns, and previews every row before it
          commits. Your roster comes over intact.
        </p>
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          {WEEKEND_STEPS.map((s) => (
            <Card key={s.n}>
              <div className="px-5 py-5">
                <div className="font-display font-black text-[28px] text-accent tracking-[-0.03em]">
                  {s.n}
                </div>
                <div className="font-display font-extrabold text-[16px] mt-1 tracking-[-0.01em]">
                  {s.title}
                </div>
                <p className="text-sub text-sm mt-2">{s.body}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Migration-included offer */}
      <section className="mt-14 border-t border-line pt-12">
        <Card>
          <div className="px-6 py-6 lg:px-10 lg:py-8 flex flex-col md:flex-row md:items-center gap-5 justify-between">
            <div className="max-w-[640px]">
              <Pill tone="ok" size="sm">
                Migration included · every plan
              </Pill>
              <div className="font-display font-black text-[24px] tracking-[-0.02em] mt-3">
                We do the migration for you — included, no setup fee.
              </div>
              <p className="text-sub text-sm mt-2">
                The NestPlot team moves your {spec.competitor} roster over with
                you in one session. Start on a 30-day free trial, no card; after
                that it's flat pricing from $59/mo.
              </p>
            </div>
            <Link to="/signup" className="shrink-0">
              <Btn kind="primary" size="lg">
                Start your 30 days
              </Btn>
            </Link>
          </div>
        </Card>
      </section>

      {/* FAQ */}
      <section className="mt-14 border-t border-line pt-12">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          Switching from {spec.competitor}
        </div>
        <h2 className="t-display-l mt-2">Questions operators ask us</h2>
        <div className="mt-6 grid md:grid-cols-2 gap-3">
          {spec.faqs.map(([q, a]) => (
            <Card key={q}>
              <div className="px-5 py-4">
                <div className="font-semibold">{q}</div>
                <p className="text-sub text-sm mt-2">{a}</p>
              </div>
            </Card>
          ))}
        </div>
        <p className="text-sub text-xs mt-4">
          Want the feature-by-feature breakdown?{" "}
          <Link to={`/vs/${spec.vsSlug}`} className="text-accent font-semibold">
            NestPlot vs {spec.competitor}, line by line →
          </Link>
        </p>
      </section>

      {/* Lead capture + final CTA */}
      <section className="mt-14 border-t border-line pt-12">
        <div className="grid md:grid-cols-[1.1fr_1fr] gap-8 items-start">
          <div>
            <h2 className="t-display-l">Get your migration plan.</h2>
            <p className="text-sub mt-3">
              Tell us how many facilities you run on {spec.competitor} and we'll
              map your switch — usually a same-weekend job. We reply within one
              business day.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/signup">
                <Btn kind="primary" size="md">
                  Start free now
                </Btn>
              </Link>
            </div>
          </div>
          <MigrateLeadForm source={`displace/${spec.slug}`} competitor={spec.competitor} />
        </div>
      </section>
    </div>
  );
}

function MigrateLeadForm({ source, competitor }: { source: string; competitor: string }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [facilities, setFacilities] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      await intake.submitInquiry(
        withUtm({
          name: name.trim(),
          email: email.trim(),
          company: company.trim() || undefined,
          facility_count: facilities ? Number(facilities) : undefined,
          message: `Migration request from ${competitor}.`,
          source,
        }),
      );
      setSubmitted(true);
      toast.success("Got it — we'll be in touch within one business day.");
    } catch {
      toast.error("Send failed. Email info@nestplot.com directly.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <Card>
        <div className="px-6 py-8 text-center">
          <Icon name="check" size={22} className="text-ok mx-auto" />
          <div className="font-display font-extrabold text-[18px] uppercase tracking-[-0.01em] mt-2">
            We're on it.
          </div>
          <p className="text-sub text-sm mt-2">
            We'll map your migration and reply within one business day. Want to
            start now?{" "}
            <button
              type="button"
              onClick={() => navigate("/signup")}
              className="text-accent font-semibold"
            >
              Create your account →
            </button>
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="px-6 py-6 space-y-4">
        <div className="grid gap-4">
          <Input label="Your name" value={name} onChange={(e) => setName(e.target.value)} required />
          <Input
            label="Work email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Company" value={company} onChange={(e) => setCompany(e.target.value)} />
            <Input
              label="# of facilities"
              type="number"
              min={1}
              value={facilities}
              onChange={(e) => setFacilities(e.target.value)}
            />
          </div>
        </div>
        <Btn kind="primary" full type="submit" disabled={submitting}>
          {submitting ? "Sending…" : "Get my migration plan"}
        </Btn>
        <p className="text-sub text-[11px] text-center">
          No spam. Just your switch plan from the NestPlot team.
        </p>
      </form>
    </Card>
  );
}

// Thin route wrappers (registered in App.tsx).
export function MigrateStorEdgePage() {
  return <MigratePageBySlug slug="storedge" />;
}
export function MigrateSiteLinkPage() {
  return <MigratePageBySlug slug="sitelink" />;
}

import { findMigration } from "./data/migrations";
import { Navigate } from "react-router-dom";

function MigratePageBySlug({ slug }: { slug: string }) {
  const spec = findMigration(slug);
  if (!spec) return <Navigate to="/vs" replace />;
  return <MigratePage spec={spec} />;
}
